import { BREAKPOINTS } from '@zoe/ds-web'
import { useCallback, useMemo, type FC } from 'react'
import styled from 'styled-components'

import { BAR_LOCALES, EU_LOCALES, FAQS_LOCALES, LOCALE_GB, MEMBERSHIP_LOCALES } from '@/types/locales'
import { OPTIMIZELY_FLAGS } from '@/types/optimizely-flags'

import { useBanner } from '@/context/bannerContext'
import { useLocale } from '@/context/localeContext'

import { trackCtaClick } from '@/lib/gtm/gtmV2'
import { useOptimizelyFlagsInfo } from '@/lib/hooks/useOptimizelyFlagsInfo'
import { isGutHealthBarEnabled } from '@/lib/utils/flags'

import { BANNER_HEIGHT_DESKTOP, BANNER_HEIGHT_MOBILE, Banner } from './Banner'
import { LocaleMismatchBanner } from './LocaleMismatchBanner'
import { Navigation } from './Navigation'
import type { NavLink } from './NavigationProductsMenu'

export const StyledBannerSpacer = styled.div`
  min-height: ${BANNER_HEIGHT_DESKTOP}px;
  @media screen and (max-width: ${BREAKPOINTS.m}) {
    height: ${BANNER_HEIGHT_MOBILE}px;
  }
`

type HeaderProps = {
  ctaText?: string
  ctaHref?: string
  secondaryCtaHref?: string
  onCtaPress?: () => void
  secondaryCtaText?: string
  onSecondaryCtaPress?: () => void
  withTopBanner?: boolean
}

export const Header: FC<HeaderProps> = ({
  ctaText,
  ctaHref,
  onCtaPress,
  secondaryCtaText,
  secondaryCtaHref,
  onSecondaryCtaPress,
  withTopBanner,
}) => {
  const { bannerContent } = useBanner()
  const { currentLocale, baseLocale } = useLocale()
  const { isFlagVariant } = useOptimizelyFlagsInfo()

  const isGhtNavVariant = isFlagVariant(OPTIMIZELY_FLAGS.E045_V001_GHT_USER_JOURNEY_NAVIGATION, 'variation-1')

  const trackNavItemClick = useCallback((label: string) => {
    trackCtaClick('navbar_header', label, { link_position: 'header' })
  }, [])

  const trackNavCtaClick = useCallback(() => {
    trackCtaClick('navbar_header', ctaText || '', { link_position: 'header' })
  }, [ctaText])

  const trackNavSecondaryCtaClick = useCallback(() => {
    trackCtaClick('navbar_header', secondaryCtaText || '', { link_position: 'header' })
  }, [secondaryCtaText])

  const trackProductsOpen = useCallback(() => {
    trackCtaClick('navbar_header', 'Products', { link_position: 'header' })
  }, [])

  const handleMenuPress = useCallback(() => trackNavItemClick('Menu'), [trackNavItemClick])
  const handleLogoPress = useCallback(() => trackNavItemClick('Logo'), [trackNavItemClick])

  // Products grouped under a single dropdown/accordion. Only the ZOE App label
  // changed; its href and visibility are unchanged.
  const productItems: NavLink[] = useMemo(
    () => [
      {
        text: 'Daily30',
        href: `/${currentLocale}/daily30`,
        onPress: () => trackNavItemClick('Daily30'),
      },
      ...(MEMBERSHIP_LOCALES.includes(currentLocale) || EU_LOCALES.includes(currentLocale)
        ? [
            {
              text: 'ZOE App',
              href: `/${baseLocale}/buymembership`,
              onPress: () => trackNavItemClick('ZOE App'),
            },
          ]
        : []),
      ...(isGutHealthBarEnabled() && (BAR_LOCALES.includes(currentLocale) || EU_LOCALES.includes(currentLocale))
        ? [
            {
              text: 'Gut Health Bar',
              // Locale-prefix to avoid next/link emitting ?locale=buyguthealthbar
              // on dynamic-route pages (see localeContext note). Use the
              // visitor's locale when supported, else fall back to GB — non-BAR
              // visitors get the in-page BarsNoCheckoutCountryWarning + waitlist CTA.
              href: `/${BAR_LOCALES.includes(currentLocale) ? currentLocale : LOCALE_GB}/buyguthealthbar`,
              onPress: () => trackNavItemClick('Gut Health Bar'),
              badge: 'New',
            },
          ]
        : []),
      ...(currentLocale === LOCALE_GB
        ? [
            {
              text: 'Gut Health Test',
              href: isGhtNavVariant ? `/${baseLocale}/buyguthealthtest` : `/${baseLocale}/buymembership`,
              onPress: () => trackNavItemClick('Gut Health Test'),
              // Only while it shares the ZOE App href — in the variant it has its own
              // destination and should highlight as active in its own right.
              disableActive: !isGhtNavVariant,
            },
          ]
        : []),
    ],
    [currentLocale, baseLocale, trackNavItemClick, isGhtNavVariant],
  )

  const navigationLinks: NavLink[] = useMemo(
    () => [
      {
        href: '/our-science',
        onPress: () => trackNavItemClick('Science'),
        text: 'Science',
      },
      {
        href: '/learn',
        onPress: () => trackNavItemClick('Learn'),
        text: 'Learn',
      },
      {
        href: '/menoscale',
        onPress: () => trackNavItemClick('MenoScale'),
        text: 'MenoScale',
      },
      {
        href: '/guthealthchallenge',
        onPress: () => trackNavItemClick('Watch the documentary'),
        text: 'Watch the documentary',
      },
      ...(FAQS_LOCALES.includes(currentLocale)
        ? [
            {
              href: `/${currentLocale}/faqs`,
              onPress: () => trackNavItemClick('FAQs'),
              text: 'FAQs',
            },
          ]
        : []),
    ],
    [currentLocale, trackNavItemClick],
  )

  return (
    <>
      <noscript
        dangerouslySetInnerHTML={{
          __html: `<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-NFJ9482N"
        height="0" width="0" style="display:none;visibility:hidden"></iframe>`,
        }}
      ></noscript>
      {bannerContent?.enabled && bannerContent?.text && <Banner />}
      <LocaleMismatchBanner />
      <Navigation
        secondaryCtaHref={secondaryCtaHref}
        secondaryCtaLabel={secondaryCtaText}
        logoVariant="brand"
        onMenuPress={handleMenuPress}
        onLogoPress={handleLogoPress}
        onSecondaryCtaPress={onSecondaryCtaPress || trackNavSecondaryCtaClick}
        withTopBanner={withTopBanner || !!(bannerContent?.enabled && bannerContent?.text)}
        links={navigationLinks}
        productItems={productItems}
        onProductsToggle={trackProductsOpen}
        ctaLabel={ctaText}
        ctaHref={ctaHref}
        onCtaPress={onCtaPress || trackNavCtaClick}
      />
      {bannerContent?.enabled && bannerContent?.text && <StyledBannerSpacer />}
    </>
  )
}
